/**
 * ContagemHeader - Header component for Contagem page
 */
import React from 'react';
import { ArrowLeft, Calendar, ChevronLeft, ChevronRight, BarChart3, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Estabelecimento } from '../../types/estabelecimento';
import type { FilterOption } from '../MultiSelectFilter';

interface ContagemHeaderProps {
  id: string | undefined;
  formData: any;
  isNewContagem: boolean;
  isEditMode: boolean;
  modifiedFields: Set<string>;
  allHospitais: Estabelecimento[];
  selectedHospitalId: number;
  navigation: {
    handlePrevious: () => void;
    handleNext: () => void;
    availableJobIds: number[];
    currentIndex: number;
    handleVoltar: () => void;
  };
  onNavigateBack: () => void;

  // Props do filtro global de cliente
  globalClienteOptions?: FilterOption[];
  globalClienteFilter?: (string | number)[];
  setGlobalClienteFilter?: (ids: (string | number)[]) => void;
  showGlobalFilter?: boolean; // false para aba Agendamento
}

export const ContagemHeader: React.FC<ContagemHeaderProps> = ({
  id,
  formData,
  isNewContagem,
  isEditMode,
  modifiedFields,
  allHospitais,
  selectedHospitalId,
  navigation,
  onNavigateBack,
  // Filtro global de cliente
  globalClienteOptions,
  globalClienteFilter,
  setGlobalClienteFilter,
  showGlobalFilter = true,
}) => {
  const navigate = useNavigate();

  const selectedHospital = allHospitais?.find(h => h.id === selectedHospitalId);
  const hospitalNome = selectedHospital?.nome_fantasia || selectedHospital?.razao_social;
  const hasNavigationJobs = (navigation?.availableJobIds?.length ?? 0) > 1;

  // Navigate to Gantt Chart with the contagem date
  const handleGanttChart = () => {
    // Parse date from formData.dataHora (format: DD/MM/YYYY)
    if (formData.dataHora) {
      const parts = formData.dataHora.split('/');
      if (parts.length === 3) {
        const dateStr = `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to YYYY-MM-DD
        navigate(`/cloud/gantt-chart?date=${dateStr}`);
        return;
      }
    }
    // Fallback: navigate without date (will use today)
    navigate('/cloud/gantt-chart');
  };

  return (
    <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left: Back button + Gantt Chart + Title */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onNavigateBack}
            className="flex items-center space-x-2 text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Voltar</span>
          </button>

          {/* Gantt Chart Button */}
          {!isNewContagem && (
            <button
              onClick={handleGanttChart}
              className="flex items-center space-x-2 text-white hover:bg-white/20 px-3 py-2 rounded-lg transition"
              title="Ver Gantt Chart do dia"
            >
              <BarChart3 size={20} />
              <span className="font-medium">Gantt Chart</span>
            </button>
          )}

          <div className="h-8 w-px bg-white/30" />

          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Calendar className="text-white" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {isNewContagem ? 'Nova Contagem' : (hospitalNome || 'Hospital não selecionado')}
              </h1>
              <p className="text-white/80 text-sm">
                {isNewContagem ? '' : `Contagem #${id}`} • {formData.dataHora || '--/--/----'}
              </p>
            </div>
          </div>
        </div>

        {/* Center/Right: Global Client Filter + Navigation arrows */}
        <div className="flex items-center space-x-4">
          {/* Global Client Filter (not shown in Agendamento tab or for new contagens) */}
          {!isNewContagem && showGlobalFilter && globalClienteOptions && globalClienteOptions.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1.5 border border-white/20">
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-white/70" />
                <select
                  value={globalClienteFilter?.length === 1 ? globalClienteFilter[0] : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setGlobalClienteFilter?.([]);
                    } else {
                      setGlobalClienteFilter?.([Number(value)]);
                    }
                  }}
                  className="bg-transparent text-white text-sm border-none outline-none cursor-pointer min-w-[180px] [&>option]:text-gray-900"
                >
                  <option value="">Todos os Clientes</option>
                  {globalClienteOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Navigation arrows (for existing jobs) */}
          {!isNewContagem && hasNavigationJobs && navigation && (
            <div className="flex items-center space-x-2">
              <button
                onClick={navigation.handlePrevious}
                disabled={(navigation.currentIndex ?? 0) <= 0}
                className="flex items-center space-x-1 text-white hover:bg-white/20 px-3 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={20} />
                <span className="text-sm font-medium">Anterior</span>
              </button>
              <span className="text-white/80 text-sm px-2">
                {(navigation.currentIndex ?? 0) + 1} / {navigation.availableJobIds?.length ?? 0}
              </span>
              <button
                onClick={navigation.handleNext}
                disabled={(navigation.currentIndex ?? 0) >= (navigation.availableJobIds?.length ?? 1) - 1}
                className="flex items-center space-x-1 text-white hover:bg-white/20 px-3 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-sm font-medium">Próxima</span>
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Edit mode indicator */}
        {isEditMode && modifiedFields.size > 0 && (
          <div className="flex items-center space-x-2 bg-orange-500/30 px-3 py-1 rounded-lg">
            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
            <span className="text-white text-sm font-medium">
              {modifiedFields.size} campo(s) modificado(s)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
